import React, { useEffect, useState } from 'react';
import { BrModal } from '@govbr-ds/react-components';
import './LogError.css';
import { GovBrTable } from 'components';
import { GovBrAccordion } from 'components';
import { elasticRequests } from 'apiServices/elasticSearchAPI/elasticRequests';

// eslint-disable-next-line react/prop-types
export function LogErrorDialog({
  open,
  setModalOpen,
  messageError,
  errorData,
}) {
  const [loading, setLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState([]);
  const showValidationTable = messageError === 'VALIDATION_ERROR';

  const handleClose = () => {
    setModalOpen(false);
  };

  function formatDateTime(dateTime) {
    if (!dateTime) return null;

    // Criar uma data a partir do timestamp
    const date = new Date(dateTime);

    if (isNaN(date.getTime())) return null;

    // Ajustar para o fuso horário correto (adicionar 3 horas para corrigir o problema)
    const adjustedDate = new Date(date.getTime() + 3 * 60 * 60 * 1000);

    return adjustedDate
      .toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      .replace(',', ' - ');
  }

  const header = [
    { display: 'Data/hora', path: '@timestamp' },
    { display: 'ID do documento', path: 'local_id' },
    { display: 'Código do erro', path: 'attempt1_code' },
    { display: 'Mensagem', path: 'attempt1_payload' },
    { display: 'Detalhe do erro', path: 'validation_errors' },
  ];

  useEffect(() => {
    async function fetchValidationErrors() {
      try {
        if (!errorData || !errorData.local_id || !errorData.cnes) {
          setLoading(false);
          return;
        }

        // Construir filtro para buscar erros de validação na API api_exame_aghu
        const validationFilter = [
          {
            filter: errorData.local_id && `(local_id:"${errorData.local_id}")`,
          },
          { filter: errorData.cnes && `cnes:"${errorData.cnes}"` },
          { filter: `status:"VALIDATION_ERROR"` },
        ]
          ?.filter((el) => el.filter)
          ?.map((e) => e.filter)
          ?.join(' AND ');

        console.log(
          'Buscando erros de validação com filtro:',
          validationFilter,
        );

        // Usando a API Api_AGHU para buscar erros de validação
        // Esta API acessa o elasticsearch/api_exame_aghu/
        const validation_data = await elasticRequests.Api_AGHU({
          filter: validationFilter,
        });

        console.log(
          'Dados de validação recebidos:',
          validation_data?.hits?.hits?.length,
        );

        // Transformar os dados recebidos para exibição na tabela
        const formattedErrors = validation_data?.hits?.hits?.map((e) => {
          // Garantir que temos dados de timestamp válidos para formatação
          const timestamp = e?._source?.['@timestamp']
            ? formatDateTime(e?._source?.['@timestamp'])
            : '-';

          // Extrair local_id e garantir que não há duplicatas
          const localId = Array.isArray(e?._source?.local_id)
            ? [...new Set(e?._source?.local_id)].join(', ')
            : e?._source?.local_id;

          // Extrair código e mensagem de erro
          const errorCode = e?._source?.attempt1_code || '-';
          const errorPayload = e?._source?.attempt1_payload || '-';

          // Extrair os detalhes do erro de validação e formatar para melhor legibilidade
          let validationError =
            e?._source?.validation_errors || 'Sem detalhes disponíveis';

          // Se o erro contiver uma exception Java, extrair apenas a mensagem principal
          if (validationError.includes('java.lang.Exception:')) {
            validationError = validationError
              .split('java.lang.Exception:')[1]
              .trim();
          }

          return {
            '@timestamp': timestamp,
            local_id: localId,
            attempt1_code: errorCode,
            attempt1_payload: errorPayload,
            validation_errors: validationError,
          };
        });

        setValidationErrors(formattedErrors || []);
      } catch (err) {
        console.log('ValidationErrors', err);
      } finally {
        setLoading(false);
      }
    }

    if (open && showValidationTable && errorData) {
      fetchValidationErrors();
    } else {
      setLoading(false);
    }
  }, [open, showValidationTable, errorData]);

  const dictMessageInfo = {
    PATIENT_DEMOGRAPHICS_ERROR:
      'O registro foi rejeitado porque há diferenças nos dados principais do paciente (nome, gênero, data de nascimento ou nome da mãe) em relação ao Cadastro Nacional de Saúde.',
    PATIENT_IDENTIFIER_ERROR:
      'O registro foi rejeitado porque o paciente não tem um número válido de Cartão Nacional de Saúde (CNS) ou CPF cadastrado corretamente.',
    NETWORK_ERROR:
      'O registro não foi processado devido a problemas de conectividade.',
    SERVER_ERROR:
      'O registro não foi processado por um erro não identificado ou uma falha no sistema.',
    VALIDATION_ERROR:
      'O registro foi rejeitado porque há um erro ou inconsistência nas informações enviadas, de acordo com as regras do sistema.',
  };

  const dictAuxiliarMessageInfo = {
    PATIENT_DEMOGRAPHICS_ERROR:
      'Para corrigir: Verifique e atualize o cadastro do paciente no AGHU e tente novamente. Se necessário, consulte os dados no HU Digital.',
    PATIENT_IDENTIFIER_ERROR:
      'Para corrigir: Atualize o cadastro do paciente no AGHU, garantindo que pelo menos um dos identificadores esteja correto. Se necessário, consulte os dados no HU Digital.',
    NETWORK_ERROR:
      'Para corrigir: Não é necessário reprocessar manualmente, pois o sistema tentará reenviar o registro automaticamente até a conexão ser restabelecida.',
    SERVER_ERROR:
      'Para corrigir: Verifique se os serviços do HU Digital estão funcionando corretamente. Se o problema continuar, entre em contato com o suporte.',
    VALIDATION_ERROR:
      'Para corrigir: Ajuste os dados do atendimento no AGHU e tente novamente. Se precisar de mais detalhes sobre o problema, consulte as ferramentas de monitoramento ou entre em contato com o suporte do HU Digital.',
  };

  return (
    <BrModal
      title="Resultado do processamento"
      isOpen={open}
      showClose
      onClose={handleClose}
    >
      <div style={{ padding: '40px 20px', lineHeight: '20px' }}>
        <h1>{dictMessageInfo[messageError]}</h1>
        <hr />
        <p>{dictAuxiliarMessageInfo[messageError]}</p>

        {showValidationTable && (
          <div style={{ marginTop: '20px' }}>
            <GovBrAccordion
              name="Detalhes dos erros de validação"
              content={
                !loading && (
                  <div style={{ minWidth: '900px' }}>
                    <GovBrTable header={header} body={validationErrors} />
                  </div>
                )
              }
              defaultExpanded={true}
              total={validationErrors.length}
            />
          </div>
        )}
      </div>
    </BrModal>
  );
}
